<template>
  <v-dialog v-model="internalValue" max-width="600px" persistent scrollable>
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-pill-multiple</v-icon>
        <span class="ds-dialog-title">Posologia do Medicamento</span>
      </v-card-title>

      <!-- Os campos usavam props do Vuetify 3 (variant/density), que o
           Vuetify 2 ignora: saíam sem contorno, fora do padrão das telas. -->
      <v-card-text class="ds-dialog-body">
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-pill</v-icon>
              <span>Dose e uso</span>
            </div>
          </div>

          <!-- Item vindo do prontuario nao tem apresentacao: sem o join, a
               linha terminava num "|" solto. -->
          <div class="med-identity mb-3">
            <strong>{{ medication?.name }}</strong>
            <span v-if="resumo" class="med-identity__resumo">{{ resumo }}</span>
          </div>

          <v-row dense>
            <v-col cols="4" md="3">
              <v-text-field
                v-model="details.quantidade_num"
                label="Qtd *"
                type="number"
                min="1"
                outlined
                dense
                color="primary"
                prepend-inner-icon="mdi-numeric"
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col cols="8" md="4">
              <v-select
                v-model="details.quantidade_unidade"
                :items="unidadesOptions"
                label="Unidade *"
                outlined
                dense
                color="primary"
                prepend-inner-icon="mdi-pill"
                hide-details="auto"
              ></v-select>
            </v-col>

            <v-col cols="12" md="5">
              <v-select
                v-model="details.frequencia"
                :items="frequenciaOptions"
                label="Frequência *"
                outlined
                dense
                color="primary"
                prepend-inner-icon="mdi-clock-outline"
                hide-details="auto"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="details.duracao"
                :items="duracaoOptions"
                label="Duração *"
                outlined
                dense
                color="primary"
                prepend-inner-icon="mdi-calendar"
                hide-details="auto"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="details.via_administracao"
                :items="viaAdministracaoOptions"
                label="Via de Administração"
                outlined
                dense
                color="primary"
                prepend-inner-icon="mdi-needle"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
        </section>

        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-note-text</v-icon>
              <span>Observações</span>
              <span class="ds-form-section__optional">opcional</span>
            </div>
          </div>
          <v-textarea
            v-model="details.observacoes"
            placeholder="Orientações ao paciente, cuidados…"
            outlined
            dense
            color="primary"
            rows="2"
            auto-grow
            prepend-inner-icon="mdi-pencil"
            hide-details="auto"
          ></v-textarea>
        </section>

        <!-- Diagnósticos (CID) deste medicamento — o seletor traz o próprio título. -->
        <section v-if="patientId" class="ds-form-section">
          <PatientCidSelector
            v-model="details.condition_ids"
            :patient-id="patientId"
          />
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" @click="handleCancel">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" depressed @click="handleConfirm" :disabled="!isValid">
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  frequenciaOptions,
  duracaoOptions,
  viaAdministracaoOptions,
} from "@/features/patients/prescriptions/utils/medicationOptions.js";
import PatientCidSelector from "@/features/patients/prescriptions/components/PatientCidSelector.vue";

export default {
  name: "MedicationDetailsDialog",

  components: { PatientCidSelector },

  props: {
    value: Boolean,
    medication: Object,
    patientId: { type: [String, Number], default: null },
  },

  data() {
    return {
      frequenciaOptions,
      duracaoOptions,
      viaAdministracaoOptions,

      unidadesOptions: [
        "comprimido(s)",
        "cápsula(s)",
        "gota(s)",
        "ml",
        "ampola(s)",
        "sachê(s)",
      ],

      details: {
        quantidade_num: "",
        quantidade_unidade: "",
        frequencia: "",
        duracao: "",
        via_administracao: "Oral",
        observacoes: "",
        condition_ids: [],
      },
    };
  },

  computed: {
    resumo() {
      return [this.medication?.dosage, this.medication?.presentation]
        .filter(Boolean)
        .join(" | ");
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    // ✅ validação mais clara
    isValid() {
      return (
        this.details.quantidade_num &&
        this.details.quantidade_unidade &&
        this.details.frequencia &&
        this.details.duracao
      );
    },
  },

  watch: {
    value(newVal) {
      if (newVal) this.resetDetails();
    },
  },

  methods: {
    resetDetails() {
      this.details = {
        quantidade_num: "",
        quantidade_unidade: "",
        frequencia: "",
        duracao: "",
        via_administracao: "Oral",
        observacoes: "",
        condition_ids: [],
      };
    },

    handleCancel() {
      this.$emit("input", false);
      this.resetDetails();
    },

    handleConfirm() {
      const quantidade = `${this.details.quantidade_num} ${this.details.quantidade_unidade}`;

      this.$emit("confirm", {
        ...this.details,
        quantidade,
      });

      this.resetDetails();
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.med-identity {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  font-size: 14px;
  color: var(--ds-text);
}
.med-identity__resumo {
  font-size: 12px;
  color: var(--ds-text-soft);
}
</style>
